/**
 * Deep Dive Analysis Utility Functions
 * Provides data transformation and analysis functions for the Deep Dive tab
 */

/**
 * Ethnicity color palette for consistent visualization
 */
export const ETHNICITY_COLORS = {
  'White': '#1976d2',
  'Black': '#9c27b0',
  'Hispanic': '#f57c00',
  'Asian/Pacific Islander': '#388e3c',
  'Other': '#757575'
};

/**
 * Quadrant thresholds for audience segmentation
 */
export const QUADRANT_THRESHOLDS = {
  aware: 50,
  interested: 50,
  theater: 40,
  payment: 60
};

/**
 * Quadrant color scheme
 */
export const QUADRANT_COLORS = {
  ideal: '#4caf50',      // Green - Top performers
  opportunity: '#ffc107', // Yellow - Growth potential
  caution: '#ff9800',    // Orange - Needs attention
  challenge: '#f44336'   // Red - Problem areas
};

/**
 * Calculate heat map data from survey responses
 * @param {Array} data - Survey data grouped by ethnicity
 * @returns {Array} Heat map formatted data
 */
export const calculateHeatMapData = (data) => {
  if (!data || data.length === 0) return [];

  const ethnicityGroups = ['White', 'Black', 'Hispanic', 'Asian/Pacific Islander', 'Other'];
  
  return ethnicityGroups.map(ethnicity => {
    const ethnicityData = data.find(d => d.ethnicity === ethnicity);
    
    if (!ethnicityData) {
      return {
        ethnicity,
        awareness: 0,
        interest: 0,
        theater: 0,
        payment: 0,
        heat: 0,
        plf: 0,
        responses: 0
      };
    }

    return {
      ethnicity,
      awareness: Math.round(ethnicityData.PctAware || 0),
      interest: Math.round(ethnicityData.PctInterest || 0),
      theater: Math.round(ethnicityData.PctTheater || 0),
      payment: Math.round(ethnicityData.PctWillingToPay || 0),
      heat: Math.round(ethnicityData.PctHeat || 0),
      plf: Math.round(ethnicityData.PctPLF || 0),
      responses: ethnicityData.totalResponses || 0
    };
  });
};

/**
 * Assign audience to aware/interested quadrants
 * @param {number} pctAware - Percentage aware (0-100)
 * @param {number} pctInterested - Percentage interested (0-100)
 * @returns {string} Quadrant name
 */
export const assignAwareInterestedQuadrant = (pctAware, pctInterested) => {
  const aware = pctAware >= QUADRANT_THRESHOLDS.aware;
  const interested = pctInterested >= QUADRANT_THRESHOLDS.interested;

  if (aware && interested) return 'Prime Audience';
  if (aware && !interested) return 'Resistant';
  if (!aware && interested) return 'Hidden Opportunity';
  return 'Cold Audience';
};

/**
 * Assign audience to theater/payment quadrants
 * @param {number} pctTheater - Percentage theater intent (0-100)
 * @param {number} pctPayment - Percentage willing to pay (0-100)
 * @returns {string} Quadrant name
 */
export const assignTheaterPaymentQuadrant = (pctTheater, pctPayment) => {
  const theater = pctTheater >= QUADRANT_THRESHOLDS.theater;
  const payment = pctPayment >= QUADRANT_THRESHOLDS.payment;

  if (theater && payment) return 'Ideal Theatrical';
  if (!theater && payment) return 'Streaming Opportunity';
  if (theater && !payment) return 'Promotional Target';
  return 'Marketing Challenge';
};

/**
 * Calculate quadrant distribution from survey data
 * @param {Array} data - Survey data
 * @param {string} quadrantType - 'aware-interested' or 'theater-payment'
 * @returns {Object} Quadrant distribution with demographic breakdowns
 */
export const calculateQuadrantDistribution = (data, quadrantType = 'aware-interested') => {
  if (!data || data.length === 0) return {};

  const quadrants = {};

  data.forEach(record => {
    let quadrant;
    
    if (quadrantType === 'aware-interested') {
      quadrant = assignAwareInterestedQuadrant(
        record.PctAware || 0,
        record.PctInterest || 0
      );
    } else {
      quadrant = assignTheaterPaymentQuadrant(
        record.PctTheater || 0,
        record.PctWillingToPay || 0
      );
    }

    if (!quadrants[quadrant]) {
      quadrants[quadrant] = {
        totalResponses: 0,
        demographics: [],
        avgAware: 0,
        avgInterest: 0,
        avgTheater: 0,
        avgPayment: 0
      };
    }

    quadrants[quadrant].totalResponses += record.totalResponses || 0;
    quadrants[quadrant].demographics.push({
      ethnicity: record.ethnicity || 'Unknown',
      agegroup: record.agegroup || 'Unknown',
      gender: record.gender || 'Unknown',
      movieFrequency: record.movie_frequency_category || 'Unknown',
      responses: record.totalResponses || 0,
      pctAware: record.PctAware || 0,
      pctInterest: record.PctInterest || 0,
      pctTheater: record.PctTheater || 0,
      pctPayment: record.PctWillingToPay || 0
    });
  });

  // Calculate averages for each quadrant
  Object.keys(quadrants).forEach(quadrant => {
    const demos = quadrants[quadrant].demographics;
    const count = demos.length;
    
    if (count > 0) {
      quadrants[quadrant].avgAware = Math.round(
        demos.reduce((sum, d) => sum + d.pctAware, 0) / count
      );
      quadrants[quadrant].avgInterest = Math.round(
        demos.reduce((sum, d) => sum + d.pctInterest, 0) / count
      );
      quadrants[quadrant].avgTheater = Math.round(
        demos.reduce((sum, d) => sum + d.pctTheater, 0) / count
      );
      quadrants[quadrant].avgPayment = Math.round(
        demos.reduce((sum, d) => sum + d.pctPayment, 0) / count
      );
    }
  });

  return quadrants;
};

/**
 * Calculate trend velocity (acceleration/deceleration)
 * @param {Array} timeSeriesData - Array of data points sorted by time
 * @param {string} metric - Metric to analyze (e.g., 'pctAware')
 * @returns {Object} Velocity analysis
 */
export const calculateTrendVelocity = (timeSeriesData, metric) => {
  if (!timeSeriesData || timeSeriesData.length < 2) {
    return { velocity: 0, direction: 'stable', acceleration: 0 };
  }

  // Calculate differences between consecutive points
  const deltas = [];
  for (let i = 1; i < timeSeriesData.length; i++) {
    const current = timeSeriesData[i][metric] || 0;
    const previous = timeSeriesData[i - 1][metric] || 0;
    deltas.push(current - previous);
  }

  // Average velocity
  const avgVelocity = deltas.reduce((sum, d) => sum + d, 0) / deltas.length;

  // Calculate acceleration (change in velocity)
  let acceleration = 0;
  if (deltas.length > 1) {
    const recentVelocity = deltas[deltas.length - 1];
    const earlierVelocity = deltas[0];
    acceleration = recentVelocity - earlierVelocity;
  }

  // Determine direction
  let direction = 'stable';
  if (avgVelocity > 1) direction = 'increasing';
  if (avgVelocity < -1) direction = 'decreasing';

  return {
    velocity: Math.round(avgVelocity * 10) / 10,
    direction,
    acceleration: Math.round(acceleration * 10) / 10,
    latestChange: deltas[deltas.length - 1] || 0
  };
};

/**
 * Transform data for ethnicity trend charts
 * @param {Array} data - Survey data with FilmRecencyOrder
 * @returns {Object} Trend data organized by ethnicity
 */
export const transformEthnicityTrendData = (data) => {
  if (!data || data.length === 0) return {};

  const ethnicities = ['White', 'Black', 'Hispanic', 'Asian/Pacific Islander', 'Other'];
  const trends = {};

  ethnicities.forEach(ethnicity => {
    const ethnicityData = data
      .filter(d => d.ethnicity === ethnicity)
      .sort((a, b) => (b.FilmRecencyOrder || 0) - (a.FilmRecencyOrder || 0))
      .reverse(); // Oldest to newest

    if (ethnicityData.length > 0) {
      trends[ethnicity] = {
        data: ethnicityData.map(d => ({
          wave: d.FilmRecencyOrder,
          surveyDate: d.SurveyCompleteDate,
          awareness: d.PctAware || 0,
          interest: d.PctInterest || 0,
          theater: d.PctTheater || 0,
          payment: d.PctWillingToPay || 0,
          heat: d.PctHeat || 0
        })),
        awarenessVelocity: calculateTrendVelocity(ethnicityData, 'PctAware'),
        interestVelocity: calculateTrendVelocity(ethnicityData, 'PctInterest'),
        theaterVelocity: calculateTrendVelocity(ethnicityData, 'PctTheater'),
        paymentVelocity: calculateTrendVelocity(ethnicityData, 'PctWillingToPay')
      };
    }
  });

  return trends;
};

/**
 * Transform data for age group trend charts
 * @param {Array} data - Survey data with FilmRecencyOrder and agegroup
 * @returns {Object} Trend data organized by age group
 */
export const transformAgeTrendData = (data) => {
  if (!data || data.length === 0) return {};

  const ageGroups = ['Under 35', 'Over 35'];
  const trends = {};

  ageGroups.forEach(ageGroup => {
    const ageData = data
      .filter(d => d.agegroup === ageGroup)
      .sort((a, b) => (b.FilmRecencyOrder || 0) - (a.FilmRecencyOrder || 0))
      .reverse(); // Oldest to newest

    if (ageData.length > 0) {
      trends[ageGroup] = {
        data: ageData.map(d => ({
          wave: d.FilmRecencyOrder,
          surveyDate: d.SurveyCompleteDate,
          awareness: d.PctAware || 0,
          interest: d.PctInterest || 0,
          theater: d.PctTheater || 0,
          payment: d.PctWillingToPay || 0,
          heat: d.PctHeat || 0
        })),
        awarenessVelocity: calculateTrendVelocity(ageData, 'PctAware'),
        interestVelocity: calculateTrendVelocity(ageData, 'PctInterest'),
        theaterVelocity: calculateTrendVelocity(ageData, 'PctTheater'),
        paymentVelocity: calculateTrendVelocity(ageData, 'PctWillingToPay')
      };
    }
  });

  return trends;
};

/**
 * Generate smart insights from data
 * @param {Object} heatMapData - Heat map data
 * @param {Object} quadrantData - Quadrant distribution data
 * @param {Object} trendData - Trend data by ethnicity
 * @param {Array} rawData - Raw API data with all demographics
 * @returns {Array} Array of insight objects
 */
export const generateSmartInsights = (heatMapData, quadrantData, trendData, rawData = []) => {
  const insights = [];

  if (!heatMapData || heatMapData.length === 0) return insights;

  // Calculate overall average for comparison
  const totalResponses = heatMapData.reduce((sum, d) => sum + d.responses, 0);
  const weightedAwareness = heatMapData.reduce(
    (sum, d) => sum + (d.awareness * d.responses), 0
  ) / totalResponses;
  const weightedTheater = heatMapData.reduce(
    (sum, d) => sum + (d.theater * d.responses), 0
  ) / totalResponses;
  const weightedInterest = heatMapData.reduce(
    (sum, d) => sum + (d.interest * d.responses), 0
  ) / totalResponses;

  // Find top performing ethnicity for baseline insights
  const sortedByInterest = [...heatMapData].sort((a, b) => b.interest - a.interest);
  const sortedByAwareness = [...heatMapData].sort((a, b) => b.awareness - a.awareness);
  const sortedByTheater = [...heatMapData].sort((a, b) => b.theater - a.theater);

  // Always show top performer
  if (sortedByInterest.length > 0 && sortedByInterest[0].interest > 0) {
    const top = sortedByInterest[0];
    insights.push({
      type: 'positive',
      category: 'Top Performer',
      ethnicity: top.ethnicity,
      message: `${top.ethnicity} audiences lead in interest at ${top.interest}%`,
      priority: top.interest
    });
  }

  // Ethnicity performance insights
  heatMapData.forEach(ethData => {
    if (ethData.responses < 5) return; // Lower threshold - Skip only very low sample sizes

    // High theater intent (lowered threshold from 10 to 5)
    const theaterDiff = ethData.theater - weightedTheater;
    if (theaterDiff > 5) {
      insights.push({
        type: 'positive',
        category: 'Theater Intent',
        ethnicity: ethData.ethnicity,
        message: `${ethData.ethnicity} audiences show ${Math.round(theaterDiff)}pp higher theater intent than average (${ethData.theater}% vs ${Math.round(weightedTheater)}%)`,
        priority: Math.abs(theaterDiff)
      });
    }

    // Low awareness opportunity (lowered threshold from -15 to -8, and interest from 40 to 25)
    const awarenessDiff = ethData.awareness - weightedAwareness;
    if (awarenessDiff < -8 && ethData.interest > 25) {
      insights.push({
        type: 'opportunity',
        category: 'Awareness Gap',
        ethnicity: ethData.ethnicity,
        message: `${ethData.ethnicity} audiences show interest (${ethData.interest}%) but lower awareness (${ethData.awareness}%) - marketing opportunity`,
        priority: Math.abs(awarenessDiff)
      });
    }

    // High heat (lowered from 30 to 20)
    if (ethData.heat > 20) {
      insights.push({
        type: 'positive',
        category: 'Heat',
        ethnicity: ethData.ethnicity,
        message: `${ethData.ethnicity} audiences show strong heat (${ethData.heat}% rating 7/7 on interest)`,
        priority: ethData.heat
      });
    }

    // High interest - new insight
    if (ethData.interest > 50) {
      insights.push({
        type: 'positive',
        category: 'Strong Interest',
        ethnicity: ethData.ethnicity,
        message: `${ethData.ethnicity} audiences show strong interest at ${ethData.interest}%`,
        priority: ethData.interest
      });
    }
  });

  // Trend insights (lowered thresholds from 5 to 3)
  if (trendData) {
    Object.keys(trendData).forEach(ethnicity => {
      const trend = trendData[ethnicity];
      
      // Accelerating awareness
      if (trend.awarenessVelocity && trend.awarenessVelocity.direction === 'increasing' && 
          trend.awarenessVelocity.velocity > 3) {
        insights.push({
          type: 'positive',
          category: 'Trend',
          ethnicity,
          message: `${ethnicity} awareness trending up (+${Math.round(trend.awarenessVelocity.latestChange)}pp in latest wave)`,
          priority: trend.awarenessVelocity.velocity
        });
      }

      // Declining interest
      if (trend.interestVelocity && trend.interestVelocity.direction === 'decreasing' && 
          trend.interestVelocity.velocity < -3) {
        insights.push({
          type: 'warning',
          category: 'Trend',
          ethnicity,
          message: `${ethnicity} interest declining (${Math.round(trend.interestVelocity.latestChange)}pp in latest wave)`,
          priority: Math.abs(trend.interestVelocity.velocity)
        });
      }
    });
  }

  // Quadrant insights - always show if data exists
  if (quadrantData && quadrantData['Prime Audience']) {
    const primeAudience = quadrantData['Prime Audience'];
    const primePercent = Math.round(
      (primeAudience.totalResponses / totalResponses) * 100
    );
    
    if (primePercent > 0) { // Always show if there's any prime audience
      insights.push({
        type: 'info',
        category: 'Audience Composition',
        message: `${primePercent}% of audience is "Prime" (both aware and interested)`,
        priority: primePercent + 50 // Boost priority so this shows near top
      });
    }
  }

  if (quadrantData && quadrantData['Hidden Opportunity']) {
    const hidden = quadrantData['Hidden Opportunity'];
    const hiddenPercent = Math.round(
      (hidden.totalResponses / totalResponses) * 100
    );
    
    if (hiddenPercent > 10) { // Lowered from 15 to 10
      insights.push({
        type: 'opportunity',
        category: 'Audience Composition',
        message: `${hiddenPercent}% interested but not yet aware - growth opportunity through awareness campaigns`,
        priority: hiddenPercent
      });
    }
  }

  // Age group insights
  if (rawData && rawData.length > 0) {
    const ageGroups = {};
    rawData.forEach(record => {
      const age = record.agegroup || 'Unknown';
      if (!ageGroups[age]) {
        ageGroups[age] = { totalResponses: 0, totalAware: 0, totalInterest: 0, totalTheater: 0 };
      }
      ageGroups[age].totalResponses += record.TotalResponses || 0;
      ageGroups[age].totalAware += record.TotalAware || 0;
      ageGroups[age].totalInterest += record.TotalInterest || 0;
      ageGroups[age].totalTheater += record.TotalTheater || 0;
    });

    Object.keys(ageGroups).forEach(age => {
      if (age === 'Unknown') return;
      const ageData = ageGroups[age];
      if (ageData.totalResponses < 5) return;

      const ageInterest = Math.round((ageData.totalInterest / ageData.totalResponses) * 100);
      const ageTheater = Math.round((ageData.totalTheater / ageData.totalResponses) * 100);

      // High interest by age
      if (ageInterest > weightedInterest + 10) {
        insights.push({
          type: 'positive',
          category: 'Age Group',
          message: `${age} audiences show elevated interest at ${ageInterest}%`,
          priority: ageInterest
        });
      }

      // High theater intent by age
      if (ageTheater > weightedTheater + 10) {
        insights.push({
          type: 'positive',
          category: 'Age Group',
          message: `${age} audiences show strong theater intent at ${ageTheater}%`,
          priority: ageTheater
        });
      }
    });

    // Gender insights
    const genders = {};
    rawData.forEach(record => {
      const gender = record.gender || 'Unknown';
      if (!genders[gender]) {
        genders[gender] = { totalResponses: 0, totalAware: 0, totalInterest: 0, totalTheater: 0 };
      }
      genders[gender].totalResponses += record.TotalResponses || 0;
      genders[gender].totalAware += record.TotalAware || 0;
      genders[gender].totalInterest += record.TotalInterest || 0;
      genders[gender].totalTheater += record.TotalTheater || 0;
    });

    Object.keys(genders).forEach(gender => {
      if (gender === 'Unknown') return;
      const genderData = genders[gender];
      if (genderData.totalResponses < 5) return;

      const genderInterest = Math.round((genderData.totalInterest / genderData.totalResponses) * 100);
      const genderTheater = Math.round((genderData.totalTheater / genderData.totalResponses) * 100);

      // High interest by gender
      if (genderInterest > weightedInterest + 10) {
        insights.push({
          type: 'positive',
          category: 'Gender',
          message: `${gender} audiences show elevated interest at ${genderInterest}%`,
          priority: genderInterest
        });
      }

      // High theater intent by gender
      if (genderTheater > weightedTheater + 10) {
        insights.push({
          type: 'positive',
          category: 'Gender',
          message: `${gender} audiences show strong theater intent at ${genderTheater}%`,
          priority: genderTheater
        });
      }
    });
  }

  // Sort by priority (descending) and return top insights
  return insights
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    .slice(0, 10); // Top 10 insights (increased from 8 to accommodate age/gender)
};

/**
 * Get color for heat map cell based on value
 * @param {number} value - Value between 0-100
 * @param {string} baseColor - Base color to use for gradient
 * @returns {string} RGB color string
 */
export const getHeatMapColor = (value, baseColor = '#1976d2') => {
  if (value === 0) return '#f5f5f5';
  
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 25, g: 118, b: 210 };
  };

  const rgb = hexToRgb(baseColor);
  const intensity = value / 100;

  // Gradient from white to color
  const r = Math.round(255 - (255 - rgb.r) * intensity);
  const g = Math.round(255 - (255 - rgb.g) * intensity);
  const b = Math.round(255 - (255 - rgb.b) * intensity);

  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Format percentage for display
 * @param {number} value - Percentage value
 * @returns {string} Formatted string
 */
export const formatPercentage = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${Math.round(value)}%`;
};

/**
 * Get velocity indicator icon/text
 * @param {Object} velocity - Velocity object from calculateTrendVelocity
 * @returns {Object} Display properties
 */
export const getVelocityIndicator = (velocity) => {
  if (!velocity) return { icon: '→', color: '#757575', text: 'Stable' };

  if (velocity.direction === 'increasing') {
    if (velocity.acceleration > 0) {
      return { icon: '⬆', color: '#4caf50', text: 'Accelerating Up' };
    }
    return { icon: '↗', color: '#8bc34a', text: 'Growing' };
  }

  if (velocity.direction === 'decreasing') {
    if (velocity.acceleration < 0) {
      return { icon: '⬇', color: '#f44336', text: 'Accelerating Down' };
    }
    return { icon: '↘', color: '#ff9800', text: 'Declining' };
  }

  return { icon: '→', color: '#757575', text: 'Stable' };
};

const deepDiveUtils = {
  ETHNICITY_COLORS,
  QUADRANT_THRESHOLDS,
  QUADRANT_COLORS,
  calculateHeatMapData,
  assignAwareInterestedQuadrant,
  assignTheaterPaymentQuadrant,
  calculateQuadrantDistribution,
  calculateTrendVelocity,
  transformEthnicityTrendData,
  transformAgeTrendData,
  generateSmartInsights,
  getHeatMapColor,
  formatPercentage,
  getVelocityIndicator
};

export default deepDiveUtils;

